import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PatientModuleLayout from "../../../components/patients/layout/PatientModuleLayout";
import {
    AdjustmentsHorizontalIcon,
    MagnifyingGlassIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import { DialogMenu } from "../components/DialogMenu";
import api from "../../../api";
import { ScheduleForm } from "./components/ScheduleForm";
import { Menu, Transition } from "@headlessui/react";
import toast, { Toaster } from "react-hot-toast";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { ScheduleTable } from "./components/ScheduleTable";
import SidebarToggleButton from "../../../components/layout/SidebarToggleButton";

export function AgendaPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const dialogRef = useRef(null);
    const navigate = useNavigate();

    const handleRowClick = (appt) => {
        navigate(`/schedule/agenda/${appt.id}/${appt.doctor.firstName} ${appt.doctor.lastName}`);
    };
    const pages = [
        { name: "Agendamientos", href: "/schedule", current: false },
        { name: "Agenda", href: "/schedule/agenda", current: true },
    ];

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
            },
            duration: 4000,
        });
    };

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const data = await api.schedules.getMulti();
            setSchedules(data);
        } catch (error) {
            console.error("Error al obtener los schedules:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchedules();
    }, []);

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            currentScreen="Agendamiento"
            header={
                <LocalHeader
                    dialogRef={dialogRef}
                    setSidebarOpen={setSidebarOpen}
                />
            }
        >
                <main className="p-6 sm:p-10 flex flex-col">
                    <Breadcrumbs pages={pages} />
                    <h2 className="text-4xl font-bold mb-10 mt-4 dark:text-gray-200">
                        Configurar agenda
                    </h2>
                    <div className="rounded-lg overflow-hidden shadow-lg border-b border-gray-200 dark:border-gray-700">
                        <ScheduleTable
                            schedules={schedules}
                            loading={loading}
                            onRowClick={handleRowClick}
                        />
                    </div>
                    <DialogMenu dialogRef={dialogRef}>
                        <ScheduleForm
                            dialogRef={dialogRef}
                            onSave={fetchSchedules}
                            notify={notify}
                        />
                    </DialogMenu>
                </main>
            <Toaster />
        </PatientModuleLayout>
    );
}

const LocalHeader = ({ dialogRef, setSidebarOpen }) => {
    return (
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
          <div className="flex min-h-[72px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <SidebarToggleButton onClick={() => setSidebarOpen(true)} />
                <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <label htmlFor="search">
                        <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-primary" />
                    </label>
                    <input
                        type="search"
                        name="search"
                        id="search"
                        className="w-full border-none bg-transparent px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-0"
                        placeholder="Buscar"
                    />
                </div>
            </div>
            <div className="hidden md:flex justify-end gap-3">
                <div className="flex gap-2">
                    <button
                        className="items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm inline-flex"
                        onClick={() => dialogRef.current?.showModal()}
                    >
                        <PlusIcon className="h-5 w-5" />
                        <span>Nuevo Horario</span>
                    </button>
                </div>
            </div>
            <div className="md:hidden flex justify-end">
                <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700">
                        <AdjustmentsHorizontalIcon className="h-6 w-6" />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-slate-200 bg-white p-4 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-900 flex flex-col gap-3">
                            <button
                                className="items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm inline-flex w-full justify-center"
                                onClick={() => dialogRef.current?.showModal()}
                            >
                                <PlusIcon className="h-5 w-5" />
                                <span>Nuevo Horario</span>
                            </button>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
          </div>
        </div>
    );
};
